import React, { useEffect, useState, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import { getData, postDataWithBody, putDataWithBody } from "../../../Services/Services";
import {
    createassignroute, getroutname, getvehicles,
    updateplannedroute, getroutlatlon, getgeofences, getAllVehicles,
} from "../../../Services/EndPoints";
import LoadingOverlay from "./LoadingOverlay";
import SearchableRouteDropdown from "./SearchableRouteDropdown";
import { useNavigate } from "react-router-dom";
import {
    GoogleMap, useJsApiLoader,
    Marker, InfoWindow,
    Polyline, Circle, Polygon,
    TransitLayer,
} from "@react-google-maps/api";
import "./RouteEnhancements.css";

// ─── Shared Google Maps loader (fixes "Loader must not be called again" error) ─
import {
    GOOGLE_MAPS_API_KEY,
    GOOGLE_MAPS_LIBRARIES,
    GOOGLE_MAPS_LOADER_ID,
} from "../../../../src/Pages/DMS/GpsTrackManagement/Googlemapsloader";

// ─── Map config ───────────────────────────────────────────────────────────────
const MAP_CONTAINER_STYLE = { height: "100%", minHeight: "560px", width: "100%" };
const DEFAULT_CENTER = { lat: 17.385044, lng: 78.486671 };
const MAP_OPTIONS = {
    mapTypeControl: false,
    streetViewControl: true,
    fullscreenControl: true,
    zoomControl: true,
    clickableIcons: false,
    gestureHandling: 'greedy',
};
const mapTypes = {
    road: "roadmap",
    satellite: "satellite",
    terrain: "terrain",
    hybrid: "hybrid",
};

const infoWindowStyle = `
  .gm-ui-hover-effect { display: none !important; }
  .gm-style-iw-chr   { display: none !important; }
  .gm-style-iw-d     { overflow: hidden !important; padding: 0 !important; }
  .gm-style-iw-c     { padding: 0 !important; border-radius: 8px !important;
                        box-shadow: 0 2px 10px rgba(0,0,0,0.2) !important; }
  .gm-style-iw       { padding: 0 !important; border-radius: 8px !important; }
`;

// ─── Layer options — inline SVGs (no lucide-react needed) ────────────────────
const LAYERS = [
    {
        id: "road", label: "Standard",
        icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/><line x1="9" y1="3" x2="9" y2="18"/><line x1="15" y1="6" x2="15" y2="21"/></svg>,
    },
    {
        id: "satellite", label: "Satellite",
        icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
    },
    {
        id: "terrain", label: "Terrain",
        icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 18 9 6 13 13 16 9 21 18"/></svg>,
    },
    {
        id: "hybrid", label: "Hybrid",
        icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/><line x1="2" y1="12" x2="22" y2="12"/></svg>,
    },
];

// ─── Inline Layers button icon ────────────────────────────────────────────────
const LayersIcon = ({ color = "#475569" }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 2 7 12 12 22 7 12 2"/>
        <polyline points="2 17 12 22 22 17"/>
        <polyline points="2 12 12 17 22 12"/>
    </svg>
);

// ─── SVG pin icons ────────────────────────────────────────────────────────────
const makePinSvg = (fillColor) => {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="44" viewBox="0 0 28 44">
    <path d="M14 0C6.268 0 0 6.268 0 14c0 5.817 3.412 10.83 8.35 13.269L14 44l5.65-16.731C24.588 24.83 28 19.817 28 14 28 6.268 21.732 0 14 0z"
      fill="${fillColor}" stroke="white" stroke-width="1.5"/>
    <circle cx="14" cy="14" r="5.5" fill="white" fill-opacity="0.85"/>
  </svg>`;
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const makeHaltSvg = () => {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 34 34">
    <circle cx="17" cy="17" r="16" fill="#FBBC04" stroke="white" stroke-width="2.5"/>
    <rect x="11" y="10" width="4" height="14" rx="1.5" fill="white"/>
    <rect x="19" y="10" width="4" height="14" rx="1.5" fill="white"/>
  </svg>`;
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const createCustomIcon = (type) => {
    if (!window.google) return undefined;
    const t = type?.toLowerCase();
    if (t === "halt") {
        return {
            url: makeHaltSvg(),
            scaledSize: new window.google.maps.Size(34, 34),
            anchor: new window.google.maps.Point(17, 17),
        };
    }
    const pinColor = t === "start" ? "#34A853" : "#EA4335";
    return {
        url: makePinSvg(pinColor),
        scaledSize: new window.google.maps.Size(28, 44),
        anchor: new window.google.maps.Point(14, 44),
    };
};

// ─── Component ────────────────────────────────────────────────────────────────
const AssignRoute = ({ userId, edit, editData, onEditModeChange }) => {
    const navigate = useNavigate();

    const { isLoaded } = useJsApiLoader({
        id: GOOGLE_MAPS_LOADER_ID,
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        libraries: GOOGLE_MAPS_LIBRARIES,
    });

    const mapRef = useRef(null);
    const geocoderRef = useRef(null);
    const autocompleteRef = useRef(null);
    const searchTimerRef = useRef(null);

    const [isLoading, setIsLoading] = useState(false);
    const [vehicles, setVehicles] = useState([]);
    const [routes, setRoutes] = useState([]);
    const [selectedRoute, setSelectedRoute] = useState(null);
    const [isLoadingWaypoints, setIsLoadingWaypoints] = useState(false);
    const [waypoints, setWaypoints] = useState([]);
    const [selectedLayer, setSelectedLayer] = useState("road");
    const [showLayerControl, setShowLayerControl] = useState(false);
    const [showTransit, setShowTransit] = useState(false);
    const [searchQuery, setSearchQuery]     = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching]     = useState(false);

    // Geofence
    const [geofences, setGeofences] = useState([]);
    const [isLoadingGeofences, setIsLoadingGeofences] = useState(false);
    const [selectedGeofence, setSelectedGeofence] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);

    const [addressCache, setAddressCache] = useState({});
    const [activeMarkerId, setActiveMarkerId] = useState(null);

    const [formData, setFormData] = useState({
        vehicleId: "",
        routeId: "",
        fromDateTime: "",
        toDateTime: "",
        fromTime: "",
        toTime: "",
        geofenceId: "",
        alertType: "",
    });

    // ── Map load ──────────────────────────────────────────────────────────────
    const onMapLoad = useCallback((map) => {
        mapRef.current = map;
        geocoderRef.current = new window.google.maps.Geocoder();
        autocompleteRef.current = new window.google.maps.places.AutocompleteService();
        map.setOptions({
            mapTypeControlOptions: {
                style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
                position: window.google.maps.ControlPosition.TOP_RIGHT,
                mapTypeIds: [
                    window.google.maps.MapTypeId.ROADMAP,
                    window.google.maps.MapTypeId.SATELLITE,
                    window.google.maps.MapTypeId.TERRAIN,
                    window.google.maps.MapTypeId.HYBRID,
                ],
            },
        });
    }, []);

    const fitMapToPoints = useCallback((points) => {
        if (!mapRef.current || !points.length) return;
        const bounds = new window.google.maps.LatLngBounds();
        points.forEach((p) =>
            bounds.extend({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) })
        );
        mapRef.current.fitBounds(bounds, { top: 20, right: 20, bottom: 20, left: 20 });
    }, []);

    // ── Map search ────────────────────────────────────────────────────────────
    const searchLocation = useCallback((query) => {
        if (!query.trim()) { setSearchResults([]); return; }
        if (!autocompleteRef.current) return;
        setIsSearching(true);
        autocompleteRef.current.getPlacePredictions(
            { input: query, componentRestrictions: { country: "in" }, types: ["geocode", "establishment"] },
            (predictions, status) => {
                setIsSearching(false);
                const OK = window.google.maps.places.PlacesServiceStatus.OK;
                if (status === OK && predictions?.length) {
                    setSearchResults(predictions.map((p) => ({ id: p.place_id, name: p.description, placeId: p.place_id })));
                } else { setSearchResults([]); }
            }
        );
    }, []);

    const selectSearchResult = useCallback((result) => {
        setSearchQuery(result.name);
        setSearchResults([]);
        if (!geocoderRef.current) return;
        geocoderRef.current.geocode({ placeId: result.placeId }, (results, status) => {
            if (status === "OK" && results[0]) {
                const loc = results[0].geometry.location;
                if (mapRef.current) {
                    if (results[0].geometry.viewport) mapRef.current.fitBounds(results[0].geometry.viewport);
                    else { mapRef.current.setCenter({ lat: loc.lat(), lng: loc.lng() }); mapRef.current.setZoom(15); }
                }
                toast.success(`Map centred on: ${result.name.split(",")[0]}`);
            }
        });
    }, []);

    const handleSearchInputChange = (e) => {
        const query = e.target.value;
        setSearchQuery(query);
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => searchLocation(query), 500);
    };

    const clearSearch = () => {
        setSearchQuery("");
        setSearchResults([]);
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };

    const reverseGeocode = useCallback((lat, lng) => {
        return new Promise((resolve) => {
            if (!geocoderRef.current) { resolve(null); return; }
            geocoderRef.current.geocode(
                { location: { lat: parseFloat(lat), lng: parseFloat(lng) } },
                (results, status) => {
                    if (status === "OK" && results[0]) {
                        const components = results[0].address_components;
                        const get = (...types) => {
                            for (const type of types) {
                                const c = components.find((ac) => ac.types.includes(type));
                                if (c) return c.long_name;
                            }
                            return null;
                        };
                        const parts = [
                            get("route", "premise", "establishment"),
                            get("sublocality_level_1", "sublocality", "neighborhood"),
                            get("locality", "administrative_area_level_3"),
                            get("administrative_area_level_1"),
                            get("postal_code"),
                        ].filter(Boolean);
                        resolve(parts.length ? parts.join(", ") : results[0].formatted_address);
                    } else {
                        resolve(null);
                    }
                }
            );
        });
    }, []);

    const fetchAddress = useCallback(async (lat, lng) => {
        const key = `${lat}_${lng}`;
        if (addressCache[key]) return;
        const address = await reverseGeocode(lat, lng);
        setAddressCache((prev) => ({ ...prev, [key]: address || "Address not found" }));
    }, [addressCache, reverseGeocode]);

    // ── Fetch master data ─────────────────────────────────────────────────────
    const fetchVehicles = async () => {
        try {
            setIsLoading(true);
            // const response = await getData(getvehicles);
            const response = await getData(getAllVehicles);
            if (Array.isArray(response)) {
                setVehicles(response.map((v) => ({
                    id: v.id, vehicleNumber: v.vehicle_number, vehicleName: v.vehicle_number,
                })));
            }
        } catch { console.error("fetchVehicles error"); }
        finally { setIsLoading(false); }
    };

    const fetchRoutes = async () => {
        try {
            const response = await getData(getroutname);
            if (response) setRoutes(response);
        } catch { console.error("fetchRoutes error"); }
    };

    const fetchGeofences = async () => {
        try {
            setIsLoadingGeofences(true);
            const response = await getData(getgeofences);
            if (Array.isArray(response)) setGeofences(response);
            else if (response?.success && Array.isArray(response.data)) setGeofences(response.data);
            else setGeofences([]);
        } catch { console.error("fetchGeofences error"); }
        finally { setIsLoadingGeofences(false); }
    };

    useEffect(() => { fetchVehicles(); fetchRoutes(); fetchGeofences(); }, []);

    // ── Edit mode ─────────────────────────────────────────────────────────────
    useEffect(() => {
        if (edit && editData) {
            setIsEditMode(true);
            if (vehicles.length > 0 && routes.length > 0) populateEditData(editData);
        } else {
            setIsEditMode(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [edit, editData, vehicles, routes, geofences]);

    // ── Fetch waypoints when route changes ────────────────────────────────────
    useEffect(() => {
        const fetchWaypoints = async () => {
            if (!selectedRoute?.routeId) { setWaypoints([]); return; }
            try {
                setIsLoadingWaypoints(true);
                const res = await getData(getroutlatlon.replace("ID", selectedRoute.routeId));
                if (res && Array.isArray(res)) {
                    setWaypoints(res);
                    if (res.length > 0 && isLoaded) setTimeout(() => fitMapToPoints(res), 150);
                } else {
                    setWaypoints([]);
                }
            } catch {
                setWaypoints([]);
                toast.error("Failed to fetch route waypoints");
            } finally {
                setIsLoadingWaypoints(false);
            }
        };
        fetchWaypoints();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRoute, isLoaded]);

    // ── Pan map to geofence ───────────────────────────────────────────────────
    useEffect(() => {
        if (!formData.geofenceId) { setSelectedGeofence(null); return; }
        const geofence = geofences.find((g) => g.id === parseInt(formData.geofenceId));
        setSelectedGeofence(geofence || null);
        if (!geofence || !mapRef.current || !isLoaded) return;
        const geo = geofence.geometry;
        if (geo.type === "Circle" && geo.center) {
            mapRef.current.setCenter({ lat: geo.center.lat, lng: geo.center.lng });
            mapRef.current.setZoom(14);
        } else if ((geo.type === "Polygon" || geo.type === "LineString") && geo.coordinates?.length) {
            const bounds = new window.google.maps.LatLngBounds();
            geo.coordinates.forEach((c) => bounds.extend({ lat: c[1], lng: c[0] }));
            mapRef.current.fitBounds(bounds, { top: 30, right: 30, bottom: 30, left: 30 });
        }
    }, [formData.geofenceId, geofences, isLoaded]);

    // ── Helpers ───────────────────────────────────────────────────────────────
    const getCurrentTime = () => {
        const now = new Date();
        return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    };

    const isToday = (dateString) => {
        if (!dateString) return false;
        return new Date().toDateString() === new Date(dateString).toDateString();
    };

    const populateEditData = (data) => {
        const parseDateTime = (dateTimeStr) => {
            const s = String(dateTimeStr).trim();
            if (s.includes(",")) {
                const [datePart, timePart] = s.split(",").map((x) => x.trim());
                const [day, month, year] = datePart.split("-");
                return { date: `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`, time: timePart.substring(0, 5) };
            }
            const dt = new Date(s);
            if (!isNaN(dt.getTime())) {
                return {
                    date: `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`,
                    time: `${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}`,
                };
            }
            return { date: "", time: "" };
        };

        const startDateTime = parseDateTime(data.startDate);
        const endDateTime = parseDateTime(data.endDate);
        const vehicle = vehicles.find((v) => v.vehicleNumber === data.vehicleNumber);
        const route = routes.find((r) => r.routeId === data.routeId);

        let resolvedGeofenceId = "";
        if (data.geofenceId != null && data.geofenceId !== "") resolvedGeofenceId = String(data.geofenceId);
        else if (Array.isArray(data.geofences) && data.geofences.length > 0) resolvedGeofenceId = String(data.geofences[0].id);

        let resolvedAlertType = "";
        if (data.alertType) resolvedAlertType = data.alertType.toUpperCase();
        else if (Array.isArray(data.geofences) && data.geofences.length > 0 && data.geofences[0].alertType) resolvedAlertType = data.geofences[0].alertType.toUpperCase();
        else if (resolvedGeofenceId && geofences.length > 0) {
            const matchedGeo = geofences.find((g) => g.id === parseInt(resolvedGeofenceId));
            if (matchedGeo?.alertType) resolvedAlertType = matchedGeo.alertType.toUpperCase();
        }

        setFormData({
            vehicleId: vehicle ? vehicle.id.toString() : "",
            routeId: route ? route.id.toString() : "",
            fromDateTime: startDateTime.date,
            toDateTime: endDateTime.date,
            fromTime: startDateTime.time,
            toTime: endDateTime.time,
            geofenceId: resolvedGeofenceId,
            alertType: resolvedAlertType,
        });

        if (route) setSelectedRoute(route);
    };

    const resetForm = () => {
        setFormData({ vehicleId: "", routeId: "", fromDateTime: "", toDateTime: "", fromTime: "", toTime: "", geofenceId: "", alertType: "" });
        setSelectedRoute(null);
        setSelectedGeofence(null);
        setWaypoints([]);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const updated = { ...formData, [name]: value };

        if (name === "geofenceId") {
            if (!value) { updated.alertType = ""; }
            else {
                const selectedGeo = geofences.find((g) => g.id === parseInt(value));
                updated.alertType = selectedGeo?.alertType ? selectedGeo.alertType.toUpperCase() : (formData.alertType || "");
            }
        }

        if (isEditMode && (name === "fromDateTime" || name === "toDateTime")) {
            const from = name === "fromDateTime" ? value : updated.fromDateTime;
            const to = name === "toDateTime" ? value : updated.toDateTime;
            if (from && to && from !== to) toast.error("From date and To date must be the same in edit mode.", { position: "top-center", autoClose: 4000 });
        }

        if (name === "fromDateTime" || name === "toDateTime") {
            if (updated.fromDateTime && updated.toDateTime) {
                const f = new Date(updated.fromDateTime); f.setHours(0, 0, 0, 0);
                const t = new Date(updated.toDateTime); t.setHours(0, 0, 0, 0);
                if (t < f) toast.error("To Date should be greater than or equal to From Date", { position: "top-center", autoClose: 4000 });
            }
        }

        if (name === "fromTime" || name === "toTime") {
            const ct = getCurrentTime();
            if (name === "fromTime" && isToday(updated.fromDateTime) && value < ct) { toast.error("From time cannot be in the past for today", { position: "top-center", autoClose: 4000 }); return; }
            if (name === "toTime" && isToday(updated.toDateTime) && value < ct) { toast.error("To time cannot be in the past for today", { position: "top-center", autoClose: 4000 }); return; }
        }

        if (updated.fromTime && updated.toTime) {
            const [fh, fm] = updated.fromTime.split(":");
            const [th, tm] = updated.toTime.split(":");
            if (parseInt(th) * 60 + parseInt(tm) <= parseInt(fh) * 60 + parseInt(fm))
                toast.error("To time must be after from time", { position: "top-center" });
        }

        setFormData(updated);
    };

    const validateAssignForm = () => {
        if (!formData.vehicleId) { toast.error("Please select a vehicle", { position: "top-center" }); return false; }
        if (!formData.routeId) { toast.error("Please select a route", { position: "top-center" }); return false; }
        if (!formData.fromDateTime) { toast.error("Please select from date", { position: "top-center" }); return false; }
        if (!formData.toDateTime) { toast.error("Please select to date", { position: "top-center" }); return false; }
        if (!formData.fromTime) { toast.error("Please select from time", { position: "top-center" }); return false; }
        if (!formData.toTime) { toast.error("Please select to time", { position: "top-center" }); return false; }
        const ct = getCurrentTime();
        if (isToday(formData.fromDateTime) && formData.fromTime < ct) { toast.error("From time cannot be in the past for today", { position: "top-center" }); return false; }
        if (isToday(formData.toDateTime) && formData.toTime < ct) { toast.error("To time cannot be in the past for today", { position: "top-center" }); return false; }
        if (isEditMode && formData.fromDateTime !== formData.toDateTime) { toast.error("From date and To date must be the same in edit mode.", { position: "top-center" }); return false; }
        const fromDate = new Date(formData.fromDateTime);
        const toDate = new Date(formData.toDateTime);
        if (formData.fromTime && formData.toTime) {
            const [fh, fm] = formData.fromTime.split(":");
            const [th, tm] = formData.toTime.split(":");
            if (parseInt(th) * 60 + parseInt(tm) <= parseInt(fh) * 60 + parseInt(fm)) { toast.error("To time must be after from time", { position: "top-center" }); return false; }
            fromDate.setHours(parseInt(fh), parseInt(fm), 0);
            toDate.setHours(parseInt(th), parseInt(tm), 0);
        }
        if (fromDate > toDate) { toast.error("To date must be after from date", { position: "top-center" }); return false; }
        return true;
    };

    const buildGeofencePayload = () => {
        if (formData.geofenceId && formData.geofenceId !== "") {
            const gf = selectedGeofence || geofences.find((g) => g.id === parseInt(formData.geofenceId));
            return { geofenceId: parseInt(formData.geofenceId), geofenceName: gf?.name || gf?.geofenceName || "", alertType: formData.alertType ? formData.alertType.toUpperCase() : "" };
        }
        return { geofenceId: null, geofenceName: "", alertType: "" };
    };

    const formatDateTime = (date, time) => {
        const d = new Date(date);
        const pad = (n) => String(n).padStart(2, "0");
        if (time) { const [h, m] = time.split(":"); d.setHours(parseInt(h), parseInt(m), 0); }
        else d.setHours(0, 0, 0);
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    };

    // ── Submit: create ────────────────────────────────────────────────────────
    const handleAssignSubmit = async () => {
        if (!validateAssignForm()) return;
        if (!waypoints.length) { toast.error("No route waypoints found. Please select a different route or try again.", { position: "top-center" }); return; }
        setIsLoading(true);
        try {
            const selectedVehicle = vehicles.find((v) => v.id === parseInt(formData.vehicleId));
            const selectedRouteData = routes.find((r) => r.id === parseInt(formData.routeId));
            const generateTimings = () => {
                const timings = [];
                const startDate = new Date(formData.fromDateTime);
                const endDate = new Date(formData.toDateTime);
                const current = new Date(startDate);
                while (current <= endDate) {
                    const dateStr = current.toISOString().split("T")[0];
                    timings.push({ fromDate: formatDateTime(dateStr, formData.fromTime), toDate: formatDateTime(dateStr, formData.toTime || formData.fromTime) });
                    current.setDate(current.getDate() + 1);
                }
                return timings;
            };
            const payload = {
                vehicleNumber: selectedVehicle?.vehicleNumber || "",
                deviceId: null,
                routeId: parseInt(selectedRouteData.routeId),
                routeName: selectedRouteData?.routeName || "",
                startDate: formatDateTime(formData.fromDateTime, formData.fromTime),
                endDate: formatDateTime(formData.toDateTime, formData.toTime || formData.fromTime),
                timings: generateTimings(),
                type: "ASSIGNED ROUTE",
                createdBy: userId,
                waypoints: waypoints.map((w) => ({ id: w.id, routeId: w.routeId, sequenceNo: w.sequenceNo, latitude: parseFloat(w.latitude), longitude: parseFloat(w.longitude), pointType: w.pointType, waitTime: w.waitTime || 0, haltDuration: w.haltDuration || null })),
                ...buildGeofencePayload(),
            };
            const response = await postDataWithBody(createassignroute, payload, {});
            if (response?.status === "success") { toast.success(response.message, { position: "top-center" }); resetForm(); }
            else toast.error(response?.message, { position: "top-center" });
        } catch { toast.error("Failed to assign route. Please try again.", { position: "top-center" }); }
        finally { setIsLoading(false); }
    };

    // ── Submit: update ────────────────────────────────────────────────────────
    const handleUpdateRoute = async () => {
        if (!validateAssignForm()) return;
        setIsLoading(true);
        try {
            const selectedRouteData = routes.find((r) => r.id === parseInt(formData.routeId));
            const geofencePayload = buildGeofencePayload();
            const payload = {
                id: editData.id,
                routeId: parseInt(selectedRouteData.routeId),
                routeName: selectedRouteData?.routeName || "",
                startDate: formatDateTime(formData.fromDateTime, formData.fromTime),
                endDate: formatDateTime(formData.toDateTime, formData.toTime),
                waypoints,
                ...geofencePayload,
            };
            const response = await putDataWithBody(updateplannedroute, payload, {});
            if (response?.status === "success") {
                toast.success(response.message || "Route updated successfully", { position: "top-center" });
                resetForm(); setIsEditMode(false);
                if (onEditModeChange) onEditModeChange(false);
                navigate("/RoutePlanning/planned-route");
            } else toast.error(response?.message, { position: "top-center" });
        } catch { toast.error("Failed to update route. Please try again.", { position: "top-center" }); }
        finally { setIsLoading(false); }
    };

    // ── Geofence overlay ──────────────────────────────────────────────────────
    const renderGeofence = () => {
        if (!selectedGeofence) return null;
        const geo = selectedGeofence.geometry;
        const color = selectedGeofence.color || "#1a7a73";

        const GeofencePopup = ({ position }) => (
            <InfoWindow position={position} onCloseClick={() => setActiveMarkerId(null)} options={{ maxWidth: 260 }}>
                <div style={{ minWidth: 210, fontFamily: "inherit", overflow: "hidden" }}>
                    <div style={{ background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)", padding: "10px 12px 9px 12px", display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ width: 30, height: 30, borderRadius: 7, background: "rgba(255,255,255,0.22)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                            </svg>
                        </div>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: "#fff" }}>{selectedGeofence.name || selectedGeofence.geofenceName}</div>
                            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.82)", marginTop: 1 }}>Geofence · {geo.type}{geo.radius ? ` · r=${geo.radius?.toFixed(0)}m` : ""}</div>
                        </div>
                        <button onClick={() => setActiveMarkerId(null)} style={{ width: 20, height: 20, borderRadius: "50%", background: "rgba(255,255,255,0.25)", border: "1px solid rgba(255,255,255,0.35)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 10, fontWeight: 700, padding: 0, flexShrink: 0 }}>✕</button>
                    </div>
                    {selectedGeofence.alertType && (
                        <div style={{ padding: "9px 12px", background: "#fff", display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>Alert Type:</span>
                            <AlertTypePill type={selectedGeofence.alertType} />
                        </div>
                    )}
                </div>
            </InfoWindow>
        );

        if (geo.type === "Circle" && geo.center) {
            return (
                <Circle center={{ lat: geo.center.lat, lng: geo.center.lng }} radius={geo.radius}
                    options={{ strokeColor: color, strokeWeight: 2, strokeOpacity: 0.9, fillColor: color, fillOpacity: 0.2 }}
                    onClick={() => setActiveMarkerId("geofence")}>
                    {activeMarkerId === "geofence" && <GeofencePopup position={{ lat: geo.center.lat, lng: geo.center.lng }} />}
                </Circle>
            );
        }

        if (geo.type === "Polygon" && geo.coordinates?.length) {
            const paths = geo.coordinates.map((c) => ({ lat: c[1], lng: c[0] }));
            const centroid = paths.reduce((acc, p) => ({ lat: acc.lat + p.lat / paths.length, lng: acc.lng + p.lng / paths.length }), { lat: 0, lng: 0 });
            return (
                <Polygon paths={paths} options={{ strokeColor: color, strokeWeight: 2, strokeOpacity: 0.9, fillColor: color, fillOpacity: 0.2 }} onClick={() => setActiveMarkerId("geofence")}>
                    {activeMarkerId === "geofence" && <GeofencePopup position={centroid} />}
                </Polygon>
            );
        }

        if (geo.type === "LineString" && geo.coordinates?.length) {
            const path = geo.coordinates.map((c) => ({ lat: c[1], lng: c[0] }));
            return <Polyline path={path} options={{ strokeColor: color, strokeWeight: 4, strokeOpacity: 0.85 }} />;
        }

        return null;
    };

    // ─── Render ───────────────────────────────────────────────────────────────
    const isSubmitDisabled = isLoading || isLoadingWaypoints || !formData.vehicleId || !formData.routeId;

    return (
        <div className="container-fluid p-0 assign-route-wrapper">
            <style>{infoWindowStyle}</style>
            <div className="row g-3" style={{ alignItems: "stretch" }}>

                {/* ── Left: Form ── */}
                <div className="col-lg-5" style={{ display: "flex", flexDirection: "column" }}>
                    <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10, position: "relative" }}>
                        {isLoading && <LoadingOverlay message="Loading data..." />}

                        {/* ── Gradient Header ── */}
                        <div style={{ background: "linear-gradient(to right, #0f3d3a 0%, #0f4a45 50%, #1a7a73 100%)", padding: "5px 14px", borderRadius: "8px 8px 0 0", display: "flex", alignItems: "center", gap: 8, boxShadow: "0 2px 8px rgba(15,76,117,0.18)" }}>
                            <div style={{ width: 26, height: 26, borderRadius: 6, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                                </svg>
                            </div>
                            <div style={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>{isEditMode ? "Edit Route Assignment" : "Assign Route to Vehicle"}</div>
                        </div>

                        {/* ── Card 1: Assignment Details ── */}
                        <div style={{ background: "#fff", border: "1px solid #e8ecf1", borderRadius: 12, padding: "16px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                                <div style={{ width: 22, height: 22, borderRadius: "50%", background: "#0f4a45", color: "#fff", fontSize: 11, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>1</div>
                                <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#fff8e1", border: "1.5px solid #f59e0b", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13" rx="2"/><path d="M16 8h4l3 5v3h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
                                </div>
                                <span style={{ fontSize: 12, fontWeight: 700, color: "#374151" }}>Assignment details</span>
                                <div style={{ flex: 1, height: 1, background: "linear-gradient(90deg, #e2e8f0 0%, transparent 100%)" }} />
                            </div>
                            {/* Vehicle */}
                            <div style={{ marginBottom: 14 }}>
                                <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px", color: "#374151", display: "block", marginBottom: 5 }}>
                                    Vehicle Number <span style={{ color: "#ef4444" }}>*</span>
                                </label>
                                <select
                                    className="form-select"
                                    name="vehicleId"
                                    value={formData.vehicleId}
                                    onChange={handleInputChange}
                                    disabled={isEditMode}
                                    style={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13, padding: "9px 12px", background: isEditMode ? "#f8fafc" : "#fff" }}
                                >
                                    <option value="">Choose vehicle</option>
                                    {vehicles.map((v) => <option key={v.id} value={v.id}>{v.vehicleNumber}</option>)}
                                </select>
                            </div>
                            {/* Route */}
                            <div style={{ marginBottom: 0 }}>
                                <SearchableRouteDropdown
                                    routes={routes}
                                    selectedRoute={selectedRoute}
                                    setSelectedRoute={(route) => {
                                        setSelectedRoute(route);
                                        setFormData((prev) => ({ ...prev, routeId: route ? route.id.toString() : "" }));
                                    }}
                                    isLoading={isLoading}
                                />
                            </div>
                        </div>

                        {/* ── Card 2: Schedule ── */}
                        <div style={{ background: "#fff", border: "1px solid #e8ecf1", borderRadius: 12, padding: "16px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                                <div style={{ width: 22, height: 22, borderRadius: "50%", background: "#0f4a45", color: "#fff", fontSize: 11, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>2</div>
                                <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#f0fdfb", border: "1.5px solid #1a7a73", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#1a7a73" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                                </div>
                                <span style={{ fontSize: 12, fontWeight: 700, color: "#374151" }}>Schedule</span>
                                <div style={{ flex: 1, height: 1, background: "linear-gradient(90deg, #e2e8f0 0%, transparent 100%)" }} />
                            </div>
                            {/* Date fields */}
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                                <div>
                                    <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px", color: "#374151", display: "block", marginBottom: 5 }}>
                                        From Date <span style={{ color: "#ef4444" }}>*</span>
                                    </label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        name="fromDateTime"
                                        value={formData.fromDateTime}
                                        onChange={handleInputChange}
                                        min={new Date().toISOString().split("T")[0]}
                                        style={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13, padding: "9px 10px" }}
                                    />
                                </div>
                                <div>
                                    <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px", color: "#374151", display: "block", marginBottom: 5 }}>
                                        To Date <span style={{ color: "#ef4444" }}>*</span>
                                    </label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        name="toDateTime"
                                        value={formData.toDateTime}
                                        onChange={handleInputChange}
                                        style={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13, padding: "9px 10px" }}
                                    />
                                </div>
                            </div>
                            {/* Time fields */}
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 0 }}>
                                <div>
                                    <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px", color: "#374151", display: "block", marginBottom: 5 }}>
                                        From Time <span style={{ color: "#ef4444" }}>*</span>
                                    </label>
                                    <input
                                        type="time"
                                        className="form-control"
                                        name="fromTime"
                                        value={formData.fromTime}
                                        onChange={handleInputChange}
                                        style={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13, padding: "9px 10px" }}
                                    />
                                </div>
                                <div>
                                    <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px", color: "#374151", display: "block", marginBottom: 5 }}>
                                        To Time <span style={{ color: "#ef4444" }}>*</span>
                                    </label>
                                    <input
                                        type="time"
                                        className="form-control"
                                        name="toTime"
                                        value={formData.toTime}
                                        onChange={handleInputChange}
                                        style={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13, padding: "9px 10px" }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* ── Card 3: Geofencing ── */}
                        <div style={{ background: "#fff", border: "1px solid #e8ecf1", borderRadius: 12, padding: "16px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                                <div style={{ width: 22, height: 22, borderRadius: "50%", background: "#0f4a45", color: "#fff", fontSize: 11, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>3</div>
                                <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#f5f3ff", border: "1.5px solid #6366f1", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                                </div>
                                <span style={{ fontSize: 12, fontWeight: 700, color: "#374151" }}>Geofencing</span>
                                <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 500 }}>(optional)</span>
                                <div style={{ flex: 1, height: 1, background: "linear-gradient(90deg, #e2e8f0 0%, transparent 100%)" }} />
                            </div>
                            {/* Geofence select */}
                            <div style={{ marginBottom: 14 }}>
                                <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px", color: "#374151", display: "block", marginBottom: 5 }}>
                                    Select Geofence
                                </label>
                                <select
                                    className="form-select"
                                    name="geofenceId"
                                    value={formData.geofenceId}
                                    onChange={handleInputChange}
                                    disabled={isLoadingGeofences}
                                    style={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13, padding: "9px 12px" }}
                                >
                                    <option value="">{isLoadingGeofences ? "Loading geofences..." : "Select geofence"}</option>
                                    {geofences.map((g) => (
                                        <option key={g.id} value={g.id}>{g.name || g.geofenceName}{g.alertType ? ` (${g.alertType})` : ""}</option>
                                    ))}
                                </select>
                                {isEditMode && formData.geofenceId && selectedGeofence && (
                                    <div style={{ marginTop: 6, padding: "6px 10px", background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 7, fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
                                        <span style={{ color: "#16a34a", fontSize: 14 }}>✓</span>
                                        <span style={{ color: "#166534", fontWeight: 600 }}>Geofence: <em>{selectedGeofence.name || selectedGeofence.geofenceName}</em> (ID: {selectedGeofence.id})</span>
                                    </div>
                                )}
                                {!isEditMode && selectedGeofence && (
                                    <div style={{ marginTop: 5, display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#16a34a", fontWeight: 600 }}>
                                        <span>✓</span><span>Geofence shown on map</span>
                                    </div>
                                )}
                            </div>
                            {/* Alert Type */}
                            <div style={{ marginBottom: 0 }}>
                                <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px", color: "#374151", display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                                    Alert Type
                                    {formData.alertType && formData.geofenceId && (
                                        <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 99, background: formData.alertType === "IN" ? "#dcfce7" : "#fee2e2", color: formData.alertType === "IN" ? "#16a34a" : "#dc2626" }}>
                                            {formData.alertType === "IN" ? "↓ IN" : "↑ OUT"}
                                        </span>
                                    )}
                                </label>
                                <select
                                    className="form-select"
                                    name="alertType"
                                    value={formData.alertType}
                                    onChange={handleInputChange}
                                    disabled={!formData.geofenceId}
                                    style={{ borderRadius: 8, border: `1px solid ${formData.alertType ? (formData.alertType === "IN" ? "#86efac" : "#fca5a5") : "#e2e8f0"}`, fontSize: 13, padding: "9px 12px", background: !formData.geofenceId ? "#f8fafc" : "#fff" }}
                                >
                                    <option value="">Select alert type</option>
                                    <option value="IN">In</option>
                                    <option value="OUT">Out</option>
                                </select>
                                {!formData.geofenceId && (
                                    <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>Select a geofence to enable alert type</div>
                                )}
                                {formData.alertType && formData.geofenceId && selectedGeofence?.alertType && (
                                    <div style={{ fontSize: 11, color: "#1a7a73", marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}>
                                        <span>ℹ</span><span>Auto-filled from geofence default — you can override this.</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* ── Action Buttons ── */}
                        <div style={{ display: "flex", gap: 8 }}>
                            <button
                                className="btn-route-secondary"
                                onClick={resetForm}
                                disabled={isLoading}
                                style={{ padding: "10px 18px", borderRadius: 10, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontSize: 13, fontWeight: 600, cursor: isLoading ? "default" : "pointer", opacity: isLoading ? 0.6 : 1, whiteSpace: "nowrap", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}
                            >
                                {isEditMode ? "Cancel" : "Reset"}
                            </button>
                            <button
                                className="btn-route-primary"
                                onClick={isEditMode ? handleUpdateRoute : handleAssignSubmit}
                                disabled={isSubmitDisabled}
                                style={{ flex: 1, padding: "10px", borderRadius: 10, border: "none", background: isSubmitDisabled ? "#5eead4" : "linear-gradient(to right, #0f3d3a 0%, #0f4a45 50%, #1a7a73 100%)", color: "#fff", fontSize: 13, fontWeight: 700, cursor: isSubmitDisabled ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 7, boxShadow: isSubmitDisabled ? "none" : "0 3px 10px rgba(26,122,115,0.3)" }}
                            >
                                {isLoading ? (
                                    <><span className="spinner-border spinner-border-sm" style={{ width: 14, height: 14 }} />{isEditMode ? "Updating..." : "Assigning..."}</>
                                ) : (
                                    <>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            {isEditMode
                                                ? <><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></>
                                                : <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></>
                                            }
                                        </svg>
                                        {isEditMode ? "Update Route" : "Assign Route"}
                                    </>
                                )}
                            </button>
                        </div>

                    </div>
                </div>

                {/* ── Right: Map ── */}
                <div className="col-lg-7" style={{ display: "flex", flexDirection: "column" }}>
                    <div className="card border-0 route-map-card" style={{ flex: 1, borderRadius: 12, overflow: "hidden", display: "flex", flexDirection: "column", minHeight: "620px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
                        {/* Map panel header */}
                        <div style={{ background: "linear-gradient(to right, #0f3d3a 0%, #0f4a45 50%, #1a7a73 100%)", padding: "5px 14px", display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                            <div style={{ width: 26, height: 26, borderRadius: 6, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                    <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/>
                                    <line x1="9" y1="3" x2="9" y2="18"/>
                                    <line x1="15" y1="6" x2="15" y2="21"/>
                                </svg>
                            </div>
                            <span style={{ color: "#fff", fontWeight: 700, fontSize: 13, flex: 1 }}>
                                {selectedRoute ? `Route Map: ${selectedRoute.routeName}` : "Route Map"}
                            </span>
                            <span style={{ background: "rgba(255,255,255,0.18)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 20, padding: "2px 9px", fontSize: 10, fontWeight: 600, flexShrink: 0 }}>
                                {(LAYERS.find(l => l.id === selectedLayer)?.label ?? selectedLayer)} layer
                            </span>
                            {isLoadingWaypoints && (
                                <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "rgba(255,255,255,0.85)" }}>
                                    <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12, borderWidth: 2 }} />
                                    Loading waypoints...
                                </span>
                            )}
                        </div>

                        {/* Search bar */}
                        <div style={{ padding: "10px 14px 0", position: "relative" }}>
                            <div style={{ display: "flex", alignItems: "center", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 9, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                                <div style={{ padding: "0 10px", color: "#94a3b8", display: "flex", alignItems: "center" }}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                                </div>
                                <input type="text" placeholder="Search city, area, landmark or coordinates…"
                                    value={searchQuery} onChange={handleSearchInputChange}
                                    style={{ flex: 1, border: "none", outline: "none", fontSize: 13, padding: "8px 4px", color: "#1e293b", background: "transparent" }} />
                                {searchQuery && (
                                    <button type="button" onClick={clearSearch}
                                        style={{ padding: "0 10px", background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 16, display: "flex", alignItems: "center" }}>✕</button>
                                )}
                                {isSearching && (
                                    <div style={{ padding: "0 10px" }}>
                                        <div className="spinner-border spinner-border-sm text-primary" role="status" style={{ width: 14, height: 14, borderWidth: 2 }} />
                                    </div>
                                )}
                            </div>
                            {searchResults.length > 0 && (
                                <div style={{ position: "absolute", left: 14, right: 14, top: "calc(100% - 2px)", zIndex: 1050, background: "#fff", borderRadius: "0 0 10px 10px", boxShadow: "0 4px 16px rgba(0,0,0,0.12)", border: "1px solid #e2e8f0", borderTop: "none", overflow: "hidden" }}>
                                    {searchResults.map((result) => (
                                        <button key={result.id} type="button" onClick={() => selectSearchResult(result)}
                                            className="search-result-row"
                                            style={{ width: "100%", padding: "10px 14px", background: "none", border: "none", borderBottom: "1px solid #f1f5f9", cursor: "pointer", textAlign: "left", display: "flex", alignItems: "flex-start", gap: 10 }}>
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: 2, flexShrink: 0 }}><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
                                            <div>
                                                <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{result.name.split(",")[0]}</div>
                                                <div style={{ fontSize: 11, color: "#64748b" }}>{result.name.split(",").slice(1, 3).join(",")}</div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="card-body p-0 position-relative" style={{ flex: 1, overflow: "hidden", minHeight: "560px" }}>
                            {isLoaded ? (
                                <GoogleMap
                                    mapContainerStyle={MAP_CONTAINER_STYLE}
                                    center={DEFAULT_CENTER}
                                    zoom={12}
                                    options={{ ...MAP_OPTIONS, mapTypeId: mapTypes[selectedLayer], minZoom: 4 }}
                                    onLoad={onMapLoad}
                                >
                                    {renderGeofence()}

                                    {waypoints.length > 1 && (
                                        <Polyline
                                            path={waypoints.map((p) => ({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) }))}
                                            options={{ strokeColor: "#1a7a73", strokeWeight: 5, strokeOpacity: 0.8 }}
                                        />
                                    )}

                                    {waypoints.filter((p) => ["start", "halt", "end"].includes(p.pointType?.toLowerCase())).map((point, idx) => {
                                        const cacheKey = `${point.latitude}_${point.longitude}`;
                                        const type = point.pointType?.toLowerCase();
                                        return (
                                            <Marker
                                                key={point.id || idx}
                                                position={{ lat: parseFloat(point.latitude), lng: parseFloat(point.longitude) }}
                                                icon={createCustomIcon(type)}
                                                onClick={async () => { await fetchAddress(point.latitude, point.longitude); setActiveMarkerId(point.id || idx); }}
                                            >
                                                {activeMarkerId === (point.id || idx) && (() => {
                                                    const ptCfgMap = {
                                                        start: { color: "#22c55e", dark: "#16a34a", tint: "#f0fdf4", label: "Start Point", sub: "Planned starting location" },
                                                        halt: { color: "#f59e0b", dark: "#d97706", tint: "#fffbeb", label: "Halt Point", sub: "Scheduled stop location" },
                                                        end: { color: "#ef4444", dark: "#dc2626", tint: "#fef2f2", label: "End Point", sub: "Planned ending location" },
                                                    };
                                                    const ptCfg = ptCfgMap[type] || { color: "#64748b", dark: "#475569", tint: "#f8fafc", label: "Way Point", sub: "" };
                                                    return (
                                                        <InfoWindow onCloseClick={() => setActiveMarkerId(null)} options={{ maxWidth: 300 }}>
                                                            <div style={{ minWidth: 260, maxWidth: 300, overflow: "hidden", fontFamily: "'Segoe UI', sans-serif" }}>
                                                                {/* Gradient header */}
                                                                <div style={{ background: `linear-gradient(135deg, ${ptCfg.color} 0%, ${ptCfg.dark} 100%)`, padding: "12px 14px 10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
                                                                    <div style={{ width: 34, height: 34, borderRadius: 8, background: "rgba(255,255,255,0.22)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                                                        {type === "halt" ? (
                                                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><rect x="6" y="4" width="4" height="16" rx="2"/><rect x="14" y="4" width="4" height="16" rx="2"/></svg>
                                                                        ) : (
                                                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5" fill="white" stroke="none"/></svg>
                                                                        )}
                                                                    </div>
                                                                    <div style={{ flex: 1 }}>
                                                                        <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{ptCfg.label}</div>
                                                                        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.82)", marginTop: 1 }}>{ptCfg.sub}</div>
                                                                    </div>
                                                                    <button onClick={() => setActiveMarkerId(null)} style={{ width: 22, height: 22, borderRadius: "50%", background: "rgba(255,255,255,0.25)", border: "1px solid rgba(255,255,255,0.35)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 11, fontWeight: 700, padding: 0, flexShrink: 0 }}>✕</button>
                                                                </div>
                                                                {/* Body */}
                                                                <div style={{ padding: "12px 14px 14px", background: "#fff" }}>
                                                                    {type === "halt" && point.waitTime > 0 && (
                                                                        <div style={{ display: "flex", alignItems: "flex-start", gap: 9, marginBottom: 10 }}>
                                                                            <div style={{ width: 26, height: 26, borderRadius: 6, background: "#fffbeb", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                                                            </div>
                                                                            <div>
                                                                                <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>Wait Duration</div>
                                                                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                                                    <div style={{ fontSize: 12, fontWeight: 700, color: "#1e293b" }}>{point.waitTime}</div>
                                                                                    <div style={{ fontSize: 10, color: "#64748b", background: "#fef3c7", borderRadius: 4, padding: "1px 6px", fontWeight: 600 }}>mins</div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                    <div style={{ display: "flex", alignItems: "flex-start", gap: 9 }}>
                                                                        <div style={{ width: 26, height: 26, borderRadius: 6, background: ptCfg.tint, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                                                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={ptCfg.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                                                                        </div>
                                                                        <div style={{ flex: 1 }}>
                                                                            <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Location</div>
                                                                            <div style={{ fontSize: 11, color: "#475569", background: ptCfg.tint, borderRadius: 6, padding: "6px 10px", borderLeft: `3px solid ${ptCfg.color}`, lineHeight: 1.5 }}>
                                                                                {addressCache[cacheKey] ? (
                                                                                    addressCache[cacheKey]
                                                                                ) : (
                                                                                    <span style={{ color: "#94a3b8", fontStyle: "italic" }}>Loading address…</span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </InfoWindow>
                                                    );
                                                })()}
                                            </Marker>
                                        );
                                    })}

                                    {showTransit && <TransitLayer />}

                                    {/* ── Map Controls Column ── */}
                                    <div style={{ position: "absolute", top: 60, right: 10, zIndex: 1000, display: "flex", flexDirection: "column", gap: 8 }}>
                                        {/* Layer Switcher */}
                                        <div style={{ position: "relative" }}>
                                            <button
                                                onClick={() => setShowLayerControl((p) => !p)}
                                                title="Switch map layer"
                                                style={{ width: 40, height: 40, borderRadius: 10, background: showLayerControl ? "#1a7a73" : "#fff", border: `1px solid ${showLayerControl ? "#1a7a73" : "#e2e8f0"}`, boxShadow: "0 2px 8px rgba(0,0,0,0.12)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                                            >
                                                <LayersIcon color={showLayerControl ? "white" : "#475569"} />
                                            </button>
                                            {showLayerControl && (
                                                <div style={{ position: "absolute", top: 46, right: 0, background: "#fff", borderRadius: 10, padding: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.14)", minWidth: 150, border: "1px solid #e2e8f0" }}>
                                                    {LAYERS.map((l) => (
                                                        <div key={l.id} onClick={() => { setSelectedLayer(l.id); setShowLayerControl(false); }}
                                                            style={{ display: "flex", alignItems: "center", gap: 9, padding: "8px 10px", borderRadius: 7, cursor: "pointer", background: selectedLayer === l.id ? "#f0fdfb" : "transparent", transition: "background 0.15s" }}>
                                                            <span style={{ color: selectedLayer === l.id ? "#1a7a73" : "#94a3b8" }}>{l.icon}</span>
                                                            <span style={{ fontSize: 13, fontWeight: selectedLayer === l.id ? 600 : 400, color: selectedLayer === l.id ? "#1a7a73" : "#334155" }}>{l.label}</span>
                                                            {selectedLayer === l.id && <span style={{ marginLeft: "auto", width: 7, height: 7, borderRadius: "50%", background: "#1a7a73" }} />}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {/* Transit Layer Toggle */}
                                        <button
                                            onClick={() => setShowTransit((p) => !p)}
                                            title={showTransit ? "Hide transit" : "Show transit"}
                                            style={{ width: 40, height: 40, borderRadius: 10, background: showTransit ? "#1a7a73" : "#fff", border: "1px solid #e2e8f0", boxShadow: "0 2px 8px rgba(0,0,0,0.12)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                                        >
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={showTransit ? "#fff" : "#555"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="4" y="2" width="16" height="16" rx="2"/>
                                                <path d="M4 10h16"/><path d="M8 2v8"/><path d="M16 2v8"/>
                                                <path d="M8 18l-2 4"/><path d="M16 18l2 4"/>
                                            </svg>
                                        </button>

                                        {/* Zoom to Route */}
                                        {waypoints.length > 0 && (
                                            <button
                                                onClick={() => fitMapToPoints(waypoints)}
                                                title="Zoom to route"
                                                style={{ width: 40, height: 40, borderRadius: 10, background: "#fff", border: "1px solid #e2e8f0", boxShadow: "0 2px 8px rgba(0,0,0,0.12)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/>
                                                    <line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/>
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                </GoogleMap>
                            ) : (
                                <div style={{ height: "100%", minHeight: 560 }} className="d-flex align-items-center justify-content-center bg-light">
                                    <div className="spinner-border text-info" role="status"><span className="visually-hidden">Loading map…</span></div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ── AlertTypePill helper ──────────────────────────────────────────────────────
const AlertTypePill = ({ type }) => {
    if (!type) return null;
    const isIn = type.toUpperCase() === "IN";
    return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 99, fontSize: 11, fontWeight: 700, background: isIn ? "#dcfce7" : "#fee2e2", color: isIn ? "#16a34a" : "#dc2626" }}>
            {isIn ? "↓ IN" : "↑ OUT"}
        </span>
    );
};

export default AssignRoute;